import { AdvancedMarker, APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../../api-client'
import ResetSidebar from '../../components/Common/ResetSidebar';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import './visits-map.css';

const EMPTY_JOB_ID_SET = new Set();

function FitMapToMarkers({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !points.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(13);
      return;
    }

    map.fitBounds(bounds, 60);
  }, [map, points]);

  return null;
}

const JobMarker = memo(function JobMarker({ id, lat, lng, clientName, selectionClassName, onToggle }) {
  const position = useMemo(() => ({ lat, lng }), [lat, lng]);
  const handleClick = useCallback(() => onToggle(id), [id, onToggle]);

  return (
    <AdvancedMarker
      position={position}
      title={clientName}
      clickable
      onClick={handleClick}
      anchorLeft="-50%"
      anchorTop="-100%"
    >
      <div className={`visits-map-marker ${selectionClassName}`} aria-label={clientName}>
        <span className="visits-map-marker__dot" />
      </div>
    </AdvancedMarker>
  )
});

const JobsMap = memo(function JobsMap({ markerPositions, styledJobIdSet, styledJobClassName, onToggleJob }) {
  return (
    <Map
      style={{ width: '100%', height: "70vh" }}
      defaultZoom={13}
      mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'}
    >
      <FitMapToMarkers points={markerPositions} />
      {markerPositions.map(({ id, lat, lng, clientName }) => {
        const selectionClassName = styledJobIdSet.has(id) ? styledJobClassName : '';

        return (
          <JobMarker
            key={id}
            id={id}
            lat={lat}
            lng={lng}
            clientName={clientName}
            selectionClassName={selectionClassName}
            onToggle={onToggleJob}
          />
        )
      })}
    </Map>
  )
}, (previousProps, nextProps) => {
  if (
    previousProps.markerPositions !== nextProps.markerPositions
    || previousProps.onToggleJob !== nextProps.onToggleJob
    || previousProps.styledJobIdSet !== nextProps.styledJobIdSet
  ) {
    return false;
  }

  return previousProps.styledJobIdSet.size === 0
    || previousProps.styledJobClassName === nextProps.styledJobClassName;
});

const JobListItem = memo(function JobListItem({ job, selectionClassName, onToggle }) {
  const handleClick = useCallback(() => onToggle(job.id), [job.id, onToggle]);

  return (
    <button
      type="button"
      className={`visits-map-list__item text-start border-0 border-bottom py-3 ${selectionClassName}`}
      onClick={handleClick}
    >
      <p className='m-0 text-wrap'>{job.client?.name} - #{job?.jobNumber} {job.title}
      </p>
    </button>
  )
});

const JobsList = memo(function JobsList({ jobs, styledJobIdSet, styledJobClassName, selectedCount, selectableJobCount, onToggleJob, onSelectAllJobs, onClearSelectedJobs }) {
  const hasJobs = selectableJobCount > 0;
  const hasSelection = selectedCount > 0;
  const isAllSelected = hasJobs && selectedCount === selectableJobCount;

  return (
    <div className='visits-map-list overflow-y-scroll d-flex flex-column'>
      <div className='d-flex justify-content-end border-bottom gap-1 p-2'>
        <button
          type="button"
          className='visits-map-list__button'
          disabled={!hasJobs || isAllSelected}
          onClick={onSelectAllJobs}
        >
          All
        </button>
        {" | "}
        <button
          type="button"
          className='visits-map-list__button'
          disabled={!hasSelection}
          onClick={onClearSelectedJobs}
        >
          None
        </button>
      </div>
      {jobs.map(job => {
        const selectionClassName = styledJobIdSet.has(job.id) ? styledJobClassName : '';

        return (
          <JobListItem
            key={job.id}
            job={job}
            selectionClassName={selectionClassName}
            onToggle={onToggleJob}
          />
        )
      })}
    </div>
  )
}, (previousProps, nextProps) => {
  if (
    previousProps.jobs !== nextProps.jobs
    || previousProps.selectedCount !== nextProps.selectedCount
    || previousProps.selectableJobCount !== nextProps.selectableJobCount
    || previousProps.onToggleJob !== nextProps.onToggleJob
    || previousProps.onSelectAllJobs !== nextProps.onSelectAllJobs
    || previousProps.onClearSelectedJobs !== nextProps.onClearSelectedJobs
    || previousProps.styledJobIdSet !== nextProps.styledJobIdSet
  ) {
    return false;
  }

  return previousProps.styledJobIdSet.size === 0
    || previousProps.styledJobClassName === nextProps.styledJobClassName;
});

function VisitsMap() {
  const [selection, setSelection] = useState(() => ({
    mode: 'partial',
    ids: EMPTY_JOB_ID_SET,
  }));

  const { data: jobs = [], error, isError, isFetching, refetch } = useQuery({
    queryKey: ['visits-map'],
    queryFn: () => apiGet('visits-map'),
    select: (data) => Array.isArray(data.jobs) ? data.jobs : [],
    retry: false,
  })

  const markerPositions = useMemo(() => {
    return jobs.map((job) => ({
      lat: Number(job.property?.address?.coordinates?.latitude),
      lng: Number(job.property?.address?.coordinates?.longitude),
      id: job.id,
      clientName: job.client?.name || 'Unnamed client'
    })).filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng)) || [];
  }, [jobs]);

  const selectableJobIds = useMemo(() => {
    return Array.from(new Set(jobs.map((job) => job.id).filter(Boolean)));
  }, [jobs]);

  const selectableJobIdSet = useMemo(() => new Set(selectableJobIds), [selectableJobIds]);

  const selectedCount = useMemo(() => {
    if (selection.mode === 'all') {
      let visibleExceptionCount = 0;
      selection.ids.forEach((jobId) => {
        if (selectableJobIdSet.has(jobId)) {
          visibleExceptionCount += 1;
        }
      });

      return Math.max(selectableJobIds.length - visibleExceptionCount, 0);
    }

    let visibleSelectedCount = 0;
    selection.ids.forEach((jobId) => {
      if (selectableJobIdSet.has(jobId)) {
        visibleSelectedCount += 1;
      }
    });

    return visibleSelectedCount;
  }, [selectableJobIds.length, selectableJobIdSet, selection]);

  const styledJobIdSet = selection.ids;
  const styledJobClassName = selection.mode === 'all' ? 'is-deselected' : 'is-selected';
  const isAllMode = selection.mode === 'all';

  const toggleSelectedJob = useCallback((jobId) => {
    setSelection((currentSelection) => {
      const nextIds = new Set(currentSelection.ids);

      if (nextIds.has(jobId)) {
        nextIds.delete(jobId);
      } else {
        nextIds.add(jobId);
      }

      return {
        mode: currentSelection.mode,
        ids: nextIds.size > 0 ? nextIds : EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const selectAllJobs = useCallback(() => {
    setSelection((currentSelection) => {
      if (currentSelection.mode === 'all' && currentSelection.ids.size === 0) {
        return currentSelection;
      }

      return {
        mode: 'all',
        ids: EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const clearSelectedJobs = useCallback(() => {
    setSelection((currentSelection) => {
      if (currentSelection.mode === 'partial' && currentSelection.ids.size === 0) {
        return currentSelection;
      }

      return {
        mode: 'partial',
        ids: EMPTY_JOB_ID_SET,
      };
    });
  }, []);

  const handleMapLoad = useCallback(() => {
    console.log('Maps API has loaded.');
  }, []);

  if (isFetching) {
    return (
      <p>Loading...</p>
    )
  }

  if (isError) {
    return (
      <>
        <ResetSidebar />
        <div className="p-3">
          <p className="mb-3">{error?.message || "Unable to load visits map."}</p>
          <button onClick={() => refetch()}>Try again</button>
        </div>
      </>
    )
  }

  return (
    <>
      <ResetSidebar />
      <APIProvider apiKey={'AIzaSyCyktXwDeLlDXsEPHdUAjCgUyXCpJGHhZE'} onLoad={handleMapLoad}>
        <div style={{ height: "100%" }} className='d-flex flex-column'>
          <div>
            <button onClick={refetch}>refetch</button>
            {selectedCount > 0 && <p>{selectedCount}</p>}
          </div>
          <div className={`visits-map-content d-flex mt-auto mb-4 ${isAllMode ? 'is-all-selected' : ''}`}>
            <JobsList
              jobs={jobs}
              styledJobIdSet={styledJobIdSet}
              styledJobClassName={styledJobClassName}
              selectedCount={selectedCount}
              selectableJobCount={selectableJobIds.length}
              onToggleJob={toggleSelectedJob}
              onSelectAllJobs={selectAllJobs}
              onClearSelectedJobs={clearSelectedJobs}
            />
            <JobsMap
              markerPositions={markerPositions}
              styledJobIdSet={styledJobIdSet}
              styledJobClassName={styledJobClassName}
              onToggleJob={toggleSelectedJob}
            />
          </div>
        </div>
      </APIProvider>
    </>
  )
}

export default VisitsMap
